import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { PaymentDetail, Transaction } from 'frontend-payments/models';
import type { Model as AuthenticatedModel } from 'frontend-payments/routes/authenticated';
import type { ModelFrom } from 'frontend-payments/utils/routes';

export type Model = ModelFrom<AuthenticatedPaymentMethodRoute>;

type Params = {
  transactionIds?: string;
};

export default class AuthenticatedPaymentMethodRoute extends Route {
  queryParams = {
    transactionIds: {
      refreshModel: true,
    },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async model(params: Params) {
    if (!params.transactionIds) {
      // If no transaction IDs provided, redirect back to overview
      this.router.transitionTo('authenticated.overview');
      return null;
    }

    const transactionIds = params.transactionIds.split(',').filter(Boolean);
    const transactions = transactionIds
      .map(
        (id: string) =>
          this.store.peekRecord('transaction', id.trim()) as Transaction,
      )
      .filter(Boolean); // Remove any null/undefined transactions

    if (transactions.length === 0) {
      // If no valid transactions found in store, redirect back to overview
      this.router.transitionTo('authenticated.overview');
      return null;
    }

    const authenticatedModel = (await this.modelFor(
      'authenticated',
    )) as AuthenticatedModel;

    // Fetch payment details
    let paymentDetails: PaymentDetail | null = null;
    const primaryTransaction = transactions[0];

    if (primaryTransaction) {
      try {
        const account = await authenticatedModel.account;
        if (account) {
          const accountNumber = account.accountNumber;
          const { transactionNumber, currency } = primaryTransaction;
          const billingCountry = account.billing.country;

          paymentDetails = await this.store.queryRecord<PaymentDetail>(
            'payment-detail',
            {
              billing_country: billingCountry,
              currency,
              adapterOptions: {
                namespace: `api/v4/accounts/${accountNumber}/transactions/${transactionNumber}`,
              },
            },
          );
        }
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    }

    return {
      account: authenticatedModel.account,
      paymentDetails,
      transactions,
    };
  }
}
